import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Form,
    InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";

export default function SocieteTaxes() {
    const navigate = useNavigate();
    const [taxes, setTaxes] = useState({
        tva_taux: 0.00,
        css_taux: 0.00,
        tva_active: true,
        css_active: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchTaxes();
    }, []);

    const fetchTaxes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setTaxes(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTaxes({
            ...taxes,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setSaving(true);
        setError(null);
        setSuccess(false);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'update_taxes',
                        ...taxes
                    })
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setError("Erreur lors de la sauvegarde");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des taxes...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-percent-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Taxes (TVA / CSS)</h4>
                                    <p className="text-muted small mb-0">Configuration des taux de taxes</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/parametres')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes modernes */}
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Taxes mises à jour avec succès
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}

                    {/* Formulaire */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                            <h5 className="mb-0 text-white">
                                <i className="ri-percent-line me-2"></i>
                                Configuration des taxes
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">
                                    {/* TVA Section */}
                                    <Col md={6}>
                                        <div className="tax-section">
                                            <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                <i className="ri-percent-line me-2"></i>
                                                TVA (Taxe sur la Valeur Ajoutée)
                                            </h6>
                                            
                                            <Form.Group className="mb-4">
                                                <div className="switch-container">
                                                    <Form.Check
                                                        type="switch"
                                                        id="tva-active"
                                                        name="tva_active"
                                                        label="Activer la TVA"
                                                        checked={taxes.tva_active}
                                                        onChange={handleChange}
                                                        className="fw-semibold"
                                                    />
                                                </div>
                                            </Form.Group>
                                            
                                            {taxes.tva_active && (
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Taux de TVA (%)</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            name="tva_taux"
                                                            value={taxes.tva_taux}
                                                            onChange={handleChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            disabled={!taxes.tva_active}
                                                            className="border-2 rounded-3"
                                                        />
                                                        <InputGroup.Text className="border-2">%</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Taux par défaut: 0%
                                                    </Form.Text>
                                                </Form.Group>
                                            )}
                                        </div>
                                    </Col>
                                    
                                    {/* CSS Section */}
                                    <Col md={6}>
                                        <div className="tax-section">
                                            <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                <i className="ri-percent-line me-2"></i>
                                                CSS (Contribution Sociale)
                                            </h6>
                                            
                                            <Form.Group className="mb-4">
                                                <div className="switch-container">
                                                    <Form.Check
                                                        type="switch"
                                                        id="css-active"
                                                        name="css_active"
                                                        label="Activer la CSS"
                                                        checked={taxes.css_active}
                                                        onChange={handleChange}
                                                        className="fw-semibold"
                                                    />
                                                </div>
                                            </Form.Group>
                                            
                                            {taxes.css_active && (
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Taux de CSS (%)</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            name="css_taux"
                                                            value={taxes.css_taux}
                                                            onChange={handleChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                            disabled={!taxes.css_active}
                                                            className="border-2 rounded-3"
                                                        />
                                                        <InputGroup.Text className="border-2">%</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted small">
                                                        Taux par défaut: 0%
                                                    </Form.Text>
                                                </Form.Group>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        className="rounded-pill px-5 py-2"
                                        disabled={saving}
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Sauvegarde...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line me-2"></i>
                                                Enregistrer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Information supplémentaire */}
                    <Card className="border-0 shadow-sm rounded-3 mt-4" style={{ background: '#f8fafc' }}>
                        <Card.Body className="p-4">
                            <h6 className="mb-3 fw-semibold">
                                <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                                Information
                            </h6>
                            <p className="small text-muted mb-0">
                                Les taux de TVA et CSS configurés ici seront appliqués par défaut lors de la création de nouveaux produits, 
                                devis, factures et bons de livraison. Vous pouvez modifier ces taux individuellement pour chaque document.
                            </p>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                
                .tax-section {
                    background: #f8fafc;
                    padding: 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                
                .tax-section:hover {
                    border-color: #21ACCD;
                    box-shadow: 0 4px 12px rgba(33, 172, 205, 0.1);
                }
                
                .switch-container .form-switch {
                    padding-left: 2.5em;
                }
                
                .switch-container .form-check-input {
                    width: 2em;
                    height: 1em;
                    cursor: pointer;
                }
                
                .switch-container .form-check-input:checked {
                    background-color: #21ACCD;
                    border-color: #21ACCD;
                }
            `}</style>
        </>
    );
}